import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { meetingsAPI } from '@/lib/api';
import { toast } from 'sonner';
import {
  Mic, MicOff, Video, VideoOff, LogOut, Copy, Users, Loader2, Link2,
} from 'lucide-react';

const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');

function ParticipantTile({ stream, name, muted, isLocal }) {
  const videoRef = useRef(null);
  const hasVideo = stream?.getVideoTracks().some(t => t.enabled);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  const initials = (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="relative rounded-xl overflow-hidden bg-slate-800 aspect-video flex items-center justify-center" data-testid={`participant-${isLocal ? 'local' : name}`}>
      {hasVideo ? (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted={isLocal}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="flex flex-col items-center gap-2">
          <div className="w-16 h-16 rounded-full bg-purple-600 flex items-center justify-center text-white text-xl font-bold">
            {initials}
          </div>
        </div>
      )}
      <div className="absolute bottom-2 left-2 flex items-center gap-1.5 px-2 py-1 rounded-lg bg-black/60 text-xs text-white">
        {muted && <MicOff className="h-3 w-3 text-red-400" />}
        <span>{isLocal ? 'Você' : name}</span>
      </div>
    </div>
  );
}

export function MeetingRoom() {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [meeting, setMeeting] = useState(null);
  const [joined, setJoined] = useState(false);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(false);
  const [localStream, setLocalStream] = useState(null);
  const [peers, setPeers] = useState({});// { peerId: { stream, name, connection } }

  const wsRef = useRef(null);
  const peerConnsRef = useRef({});
  const localStreamRef = useRef(null);

  // Load meeting info
  useEffect(() => {
    loadMeeting();
    return () => cleanup();
  }, [meetingId]);

  const loadMeeting = async () => {
    try {
      // Try to find meeting by ID or slug
      const res = await meetingsAPI.getMeeting(meetingId);
      setMeeting(res.data);
    } catch {
      toast.error('Reunião não encontrada');
      navigate('/reunioes');
    } finally {
      setLoading(false);
    }
  };

  const cleanup = useCallback(() => {
    // Close all peer connections
    Object.values(peerConnsRef.current).forEach(pc => pc.close());
    peerConnsRef.current = {};

    // Stop local stream
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }

    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setLocalStream(null);
    setPeers({});
    setJoined(false);
  }, []);

  const createPeerConnection = useCallback((peerId, isInitiator) => {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    peerConnsRef.current[peerId] = pc;

    // Add local tracks
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        pc.addTrack(track, localStreamRef.current);
      });
    }

    // Handle remote stream
    pc.ontrack = (event) => {
      const [remoteStream] = event.streams;
      setPeers(prev => ({
        ...prev,
        [peerId]: { ...prev[peerId], stream: remoteStream },
      }));
    };

    // Handle ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'ice-candidate',
          targetId: peerId,
          candidate: event.candidate,
        }));
      }
    };

    pc.oniceconnectionstatechange = () => {
      if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'disconnected') {
        // Peer disconnected
        setPeers(prev => {
          const next = { ...prev };
          delete next[peerId];
          return next;
        });
      }
    };

    return pc;
  }, []);

  const handleSignalingMessage = useCallback(async (msg) => {
    const { type, senderId } = msg;

    if (type === 'peers') {
      // Create offers to all existing peers
      for (const peerId of msg.peers) {
        setPeers(prev => ({ ...prev, [peerId]: { stream: null, name: peerId } }));
        const pc = createPeerConnection(peerId, true);
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        wsRef.current?.send(JSON.stringify({
          type: 'offer',
          targetId: peerId,
          sdp: pc.localDescription,
        }));
      }
    } else if (type === 'peer-joined') {
      setPeers(prev => ({ ...prev, [msg.peerId]: { stream: null, name: msg.peerId } }));
      // Don't create offer — wait for them to send us one
    } else if (type === 'offer') {
      const pc = createPeerConnection(senderId, false);
      await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      wsRef.current?.send(JSON.stringify({
        type: 'answer',
        targetId: senderId,
        sdp: pc.localDescription,
      }));
    } else if (type === 'answer') {
      const pc = peerConnsRef.current[senderId];
      if (pc) {
        await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
      }
    } else if (type === 'ice-candidate') {
      const pc = peerConnsRef.current[senderId];
      if (pc && msg.candidate) {
        await pc.addIceCandidate(new RTCIceCandidate(msg.candidate));
      }
    } else if (type === 'peer-left') {
      const pc = peerConnsRef.current[msg.peerId];
      if (pc) {
        pc.close();
        delete peerConnsRef.current[msg.peerId];
      }
      setPeers(prev => {
        const next = { ...prev };
        delete next[msg.peerId];
        return next;
      });
    }
  }, [createPeerConnection]);

  const joinRoom = async () => {
    if (!user || !meeting) return;

    try {
      // Join via API (registers participant)
      await meetingsAPI.joinMeeting(meeting.id);

      // Get local media (audio first, video optional)
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: camOn });
      } catch {
        // Fallback: audio only
        try {
          stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
          setCamOn(false);
          toast.info('Câmera não disponível. Entrando apenas com áudio.');
        } catch (err) {
          toast.error('Não foi possível acessar o microfone. Verifique as permissões.');
          return;
        }
      }

      localStreamRef.current = stream;
      setLocalStream(stream);

      // Connect WebSocket for signaling
      const wsUrl = `${WS_URL}/api/ws/rtc/${meeting.id}/${user.id}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setJoined(true);
      };

      ws.onmessage = (event) => {
        const msg = JSON.parse(event.data);
        handleSignalingMessage(msg);
      };

      ws.onclose = () => {
        // Only show message if we were joined (not if we're leaving intentionally)
        if (wsRef.current === ws) {
          toast.info('Conexão encerrada');
        }
      };

      ws.onerror = () => {
        toast.error('Erro na conexão');
      };

    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao entrar na sala');
    }
  };

  const leaveRoom = () => {
    cleanup();
    navigate('/reunioes');
  };

  const toggleMic = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setMicOn(audioTrack.enabled);
      }
    }
  };

  const toggleCam = async () => {
    if (!localStreamRef.current) return;

    if (camOn) {
      // Turn off camera
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.stop();
        localStreamRef.current.removeTrack(videoTrack);
      }
      setCamOn(false);
    } else {
      // Turn on camera
      try {
        const videoStream = await navigator.mediaDevices.getUserMedia({ video: true });
        const videoTrack = videoStream.getVideoTracks()[0];
        localStreamRef.current.addTrack(videoTrack);

        // Replace track in all peer connections
        Object.values(peerConnsRef.current).forEach(pc => {
          const sender = pc.getSenders().find(s => s.track?.kind === 'video');
          if (sender) {
            sender.replaceTrack(videoTrack);
          } else {
            pc.addTrack(videoTrack, localStreamRef.current);
          }
        });

        setCamOn(true);
        setLocalStream(new MediaStream(localStreamRef.current.getTracks()));
      } catch {
        toast.error('Câmera não disponível');
      }
    }
  };

  const copyInviteLink = () => {
    const link = `${window.location.origin}/reunioes/${meetingId}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copiado!');
  };

  const peerCount = Object.keys(peers).length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
      </div>
    );
  }

  if (!meeting) return null;

  // Pre-join screen
  if (!joined) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'linear-gradient(180deg, #0a0a1a 0%, #111133 50%, #0a0a1a 100%)' }} data-testid="meeting-prejoin">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-purple-600/20 flex items-center justify-center mx-auto mb-4">
            <Users className="h-8 w-8 text-purple-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">{meeting.title}</h1>
          <p className="text-sm text-gray-400 mb-6">{meeting.description || meeting.topic}</p>

          <div className="flex justify-center gap-4 mb-6">
            <button onClick={() => setMicOn(!micOn)} className={`p-3 rounded-full transition-colors ${micOn ? 'bg-white/10 text-white' : 'bg-red-500/20 text-red-400'}`} data-testid="prejoin-mic-toggle">
              {micOn ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
            </button>
            <button onClick={() => setCamOn(!camOn)} className={`p-3 rounded-full transition-colors ${camOn ? 'bg-white/10 text-white' : 'bg-white/5 text-gray-500'}`} data-testid="prejoin-cam-toggle">
              {camOn ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
            </button>
          </div>

          <button onClick={joinRoom} className="w-full py-3 rounded-xl text-base font-semibold text-white transition-all hover:brightness-110" style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' }} data-testid="join-room-btn">
            Entrar na Sala
          </button>

          <button onClick={() => navigate('/reunioes')} className="mt-3 text-sm text-gray-500 hover:text-gray-300 transition-colors">
            Voltar
          </button>
        </div>
      </div>
    );
  }

  // In-room view
  return (
    <div className="h-screen flex flex-col" style={{ background: '#0a0a1a' }} data-testid="meeting-room">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-white/5">
        <div>
          <h2 className="text-sm font-semibold text-white">{meeting.title}</h2>
          <p className="text-xs text-gray-500">{peerCount + 1} participante{peerCount > 0 ? 's' : ''}</p>
        </div>
        <button onClick={copyInviteLink} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-xs text-gray-400 transition-colors" data-testid="copy-invite-link">
          <Link2 className="h-3.5 w-3.5" /> Copiar Link
        </button>
      </div>

      {/* Participants grid */}
      <div className="flex-1 p-4 overflow-auto">
        <div className={`grid gap-3 h-full ${
          peerCount === 0 ? 'grid-cols-1 max-w-lg mx-auto' :
          peerCount <= 1 ? 'grid-cols-2' :
          peerCount <= 3 ? 'grid-cols-2' :
          'grid-cols-3'
        }`}>
          {/* Local participant */}
          <ParticipantTile stream={localStream} name={user?.name} muted={!micOn} isLocal />

          {/* Remote participants */}
          {Object.entries(peers).map(([peerId, data]) => (
            <ParticipantTile
              key={peerId}
              stream={data.stream}
              name={data.name || peerId.slice(0, 8)}
              muted={false}
              isLocal={false}
            />
          ))}
        </div>
      </div>

      {/* Controls bar */}
      <div className="flex items-center justify-center gap-3 px-4 py-4 border-t border-white/5" data-testid="meeting-controls">
        <button onClick={toggleMic} className={`p-3 rounded-full transition-colors ${micOn ? 'bg-white/10 text-white hover:bg-white/15' : 'bg-red-500 text-white'}`} data-testid="mic-toggle">
          {micOn ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
        </button>
        <button onClick={toggleCam} className={`p-3 rounded-full transition-colors ${camOn ? 'bg-white/10 text-white hover:bg-white/15' : 'bg-white/5 text-gray-500 hover:bg-white/10'}`} data-testid="cam-toggle">
          {camOn ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
        </button>
        <button onClick={copyInviteLink} className="p-3 rounded-full bg-white/5 text-gray-400 hover:bg-white/10 transition-colors" data-testid="copy-link-control">
          <Copy className="h-5 w-5" />
        </button>
        <button onClick={leaveRoom} className="p-3 rounded-full bg-red-600 text-white hover:bg-red-700 transition-colors" data-testid="leave-room-btn">
          <LogOut className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
